import { NavLink, Outlet } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { can as hasPermission, useSession } from '@/shared/auth/session'

/**
 * Communications — the module shell.
 *
 * ## Why "Replies" is a tab and not a notification badge
 *
 * Because a reply is a person answering a campaign, and the failure mode of campaign messaging
 * is that the sending is instrumented and the listening is not. A STOP is honoured by machine;
 * everything else is somebody talking to the campaign, and it needs a place that a human is
 * expected to open.
 */

interface Tab {
  to: string
  label: string
  hint: string
  permission?: string
  end?: boolean
}

export const COMMS_TABS: Tab[] = [
  { to: '/comms', label: 'Overview', end: true,
    hint: 'What is in flight, what went out today, and what came back' },
  { to: '/comms/campaigns', label: 'Campaigns',
    hint: 'Every send, and who approved it' },
  { to: '/comms/compose', label: 'Compose',
    hint: 'Build a send. Nothing leaves until somebody approves it',
    permission: 'comms:compose' },
  { to: '/comms/templates', label: 'Templates',
    hint: 'The messages campaigns send', permission: 'comms:templates' },
  { to: '/comms/suppressions', label: 'Do not contact',
    hint: 'Who must not be messaged, and why', permission: 'comms:suppress' },
  { to: '/comms/replies', label: 'Replies',
    hint: 'What people sent back. A STOP is already honoured; the rest need you',
    permission: 'comms:suppress' },
  { to: '/comms/channels', label: 'Channels',
    hint: 'Providers, sender ids, caps and costs', permission: 'comms:channels' },
]

export function CommsLayout() {
  const authorities = useSession((s) => s.authorities)
  const tabs = COMMS_TABS.filter((t) => hasPermission(authorities, t.permission))

  return (
    <div className="space-y-3">
      <nav className="axis-table-scroll flex gap-0.5 border-b border-border"
           aria-label="Communications">
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            title={tab.hint}
            className={({ isActive }) => cn(
              'shrink-0 whitespace-nowrap border-b-2 px-2.5 py-1.5 text-[12px]',
              isActive
                ? 'border-primary font-medium text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  )
}
